import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useHistory, useLocation, useParams } from 'react-router-dom';

import PrivateSubmissionCommunication from '@containers/private-crm-app/PrivateSubmissionDetailPage/PrivateSubmissionCommunication';

import FollowAuthModal from '@components/FollowAuthModal';
import Loading from '@components/Loading';

import { AuthScreen, useAuthManagerContext } from '@private-crm-app/auth-manager-context';
import { useAgencyFlags } from '@private-crm-app/hooks/useAgencyFlags';
import PrivateSubmissionLayout from '@private-crm-app/components/PrivateSubmissionLayout/PrivateSubmissionLayout';
import { CRM_ISSUE_ID, PENDING_FOLLOW_ISSUE_ID, PrivateSubmissionAppRoutes } from '@private-crm-app/constants';
import PrivateSubmissionDetails, {
  ISubmissionDetailBase,
} from '@private-crm-app/PrivateSubmissionDetailPage/PrivateSubmissionDetails';
import { fetchPublicMapIssueById, followPublicMapIssue, unfollowPublicMapIssue } from '@private-crm-app/requests/publicMap';
import { getSubmissionByCrmIssueId } from '@private-crm-app/requests/submission';
import {
  clearPendingFollowIntent,
  getCRMTokenFromUrlOrStorage,
  getPublicMapUrl,
  getStoredSubmission,
  trackPublicMapFollowClickedDetail,
  trackPublicMapSubmissionDetailViewed,
} from '@private-crm-app/utils';

import './style.scss';

const SUCCESS_TOAST_TIMEOUT = 5000;

type ViewMode = 'private' | 'public';

type FetchResult = {
  submission: ISubmissionDetailBase | null | undefined;
  viewMode: ViewMode;
  loginRedirectTriggered?: boolean;
};

function isAbortError(err: any): boolean {
  return err?.name === 'AbortError' || err?.code === 'ERR_CANCELED';
}

async function fetchAuthenticatedFlow(
  issueIdToFetch: string,
  crmToken: string | null,
  signal: AbortSignal,
  login: (screen: string) => void,
  showSubmissionCreatedToast: (data: ICustomerSubmissionResponse) => void
): Promise<FetchResult> {
  try {
    const data = await getSubmissionByCrmIssueId(issueIdToFetch, signal);
    showSubmissionCreatedToast(data);

    return { submission: data, viewMode: 'private' };
  } catch (err: any) {
    if (isAbortError(err)) {
      throw err;
    }
    const status = err?.response?.status;
    if (status === 401) {
      login(AuthScreen.ISSUE);

      return { submission: undefined, viewMode: 'private', loginRedirectTriggered: true };
    }
    if (status === 403 && crmToken) {
      try {
        const publicData = await fetchPublicMapIssueById(crmToken, issueIdToFetch, signal);

        return { submission: publicData, viewMode: 'public' };
      } catch (inner: any) {
        if (isAbortError(inner)) {
          throw inner;
        }

        return { submission: null, viewMode: 'private' };
      }
    }

    return { submission: null, viewMode: 'private' };
  }
}

async function fetchUnauthenticatedFlow(
  issueIdToFetch: string,
  crmToken: string,
  signal: AbortSignal,
  login: (screen: string) => void
): Promise<FetchResult> {
  try {
    const publicData = await fetchPublicMapIssueById(crmToken, issueIdToFetch, signal);

    return { submission: publicData, viewMode: 'public' };
  } catch (err: any) {
    if (isAbortError(err)) {
      throw err;
    }
    if (err?.response?.status === 403 && err?.response?.data?.code === 'PUBLIC_MAP_DISABLED') {
      login(AuthScreen.ISSUE);

      return { submission: undefined, viewMode: 'public', loginRedirectTriggered: true };
    }

    return { submission: null, viewMode: 'public' };
  }
}

export default function PrivateSubmissionDetailPage() {
  const history = useHistory();
  const location = useLocation<{ source?: string }>();
  const { crmIssueId: crmIssueIdFromPath } = useParams<{ crmIssueId: string }>();
  const urlParams = useMemo(() => new URLSearchParams(location.search), [location.search]);
  const crmToken = useMemo(
    () => urlParams.get('crm_token') ?? getCRMTokenFromUrlOrStorage(location.search ?? undefined),
    [urlParams, location.search]
  );
  const crmIssueIdFromQuery = urlParams.get(CRM_ISSUE_ID);
  const effectiveCrmIssueId = crmIssueIdFromPath ?? crmIssueIdFromQuery ?? undefined;

  const { isAuthenticated, isAuthResolved, login } = useAuthManagerContext();
  const { flags } = useAgencyFlags();

  const [isSubmissionLoading, setIsSubmissionLoading] = useState(true);
  const [submission, setSubmission] = useState<ISubmissionDetailBase | null | undefined>(undefined);
  const [viewMode, setViewMode] = useState<ViewMode>('private');
  const [showSuccessBanner, setShowSuccessBanner] = useState(false);
  const [isFollowAuthModalOpen, setIsFollowAuthModalOpen] = useState(false);
  const [hasPublicMessages, setHasPublicMessages] = useState(false);

  // URL normalization: add crm_token to URL if we have it but it's not in the URL
  useEffect(() => {
    if (!crmToken || !effectiveCrmIssueId) {
      return;
    }
    const urlToken = urlParams.get('crm_token');
    if (!urlToken) {
      const newSearch = new URLSearchParams(location.search);
      newSearch.set('crm_token', crmToken);
      history.replace({ pathname: location.pathname, search: `?${newSearch.toString()}` });
    }
  }, [crmToken, effectiveCrmIssueId, history, location.pathname, location.search, urlParams]);

  const showSubmissionCreatedToast = useCallback((data: ICustomerSubmissionResponse) => {
    try {
      const markerKey = `submission_created_toast_${data?.id}`;
      const localIssueId = Number(localStorage.getItem(CRM_ISSUE_ID));
      const key = localIssueId ? `submission_created_toast_${localIssueId}` : markerKey;
      const shouldToast = sessionStorage.getItem(key) === '1';

      if (shouldToast) {
        setShowSuccessBanner(true);
        sessionStorage.setItem('delayOnboarding', 'true');
        setTimeout(() => {
          setShowSuccessBanner(false);
          sessionStorage.removeItem('delayOnboarding');
        }, SUCCESS_TOAST_TIMEOUT);
        sessionStorage.removeItem(key);
        localStorage.removeItem(CRM_ISSUE_ID);
      }
    } catch (e) {
      console.log('Error setting submission created toast:', e);
    }
  }, []);

  const handleFollowClick = useCallback(() => {
    if (submission) {
      trackPublicMapFollowClickedDetail(isAuthenticated, submission.category.name, submission.issueSummary.status);
    }
    if (!isAuthenticated) {
      if (effectiveCrmIssueId) {
        localStorage.setItem(PENDING_FOLLOW_ISSUE_ID, effectiveCrmIssueId);
      }
      setIsFollowAuthModalOpen(true);
      return;
    }

    if (!crmToken || !effectiveCrmIssueId) return;

    followPublicMapIssue(crmToken, Number(effectiveCrmIssueId))
      .then(() =>
        fetchPublicMapIssueById(crmToken, effectiveCrmIssueId)
      )
      .then((refreshed) => {
        setSubmission((prev) =>
          prev ? { ...prev, ...refreshed, isFollowing: true } : prev
        );
      })
      .catch((err) => {
        console.error('[PrivateSubmissionDetailPage] Failed to follow submission', err);
      });
  }, [isAuthenticated, crmToken, effectiveCrmIssueId, submission]);

  const handleUnfollowClick = useCallback(() => {
    if (!crmToken || !effectiveCrmIssueId) return;

    unfollowPublicMapIssue(crmToken, Number(effectiveCrmIssueId))
      .then(({ followerCount }) => {
        setSubmission((prev) =>
          prev ? { ...prev, isFollowing: false, followerCount } : prev
        );
      })
      .catch((err) => {
        console.error('[PrivateSubmissionDetailPage] Failed to unfollow submission', err);
      });
  }, [crmToken, effectiveCrmIssueId]);

  const resolveEffectiveIssueId = useCallback((): string | undefined => {
    if (effectiveCrmIssueId) {
      return effectiveCrmIssueId;
    }

    if (!isAuthenticated) {
      return undefined;
    }

    const issueId = Number(localStorage.getItem(CRM_ISSUE_ID));
    if (issueId) {
      return String(issueId);
    }

    const submissionDetails = getStoredSubmission();

    return submissionDetails?.id != null ? String(submissionDetails.id) : undefined;
  }, [effectiveCrmIssueId, isAuthenticated]);

  useEffect((): void | (() => void) => {
    if (!isAuthResolved) {
      return;
    }

    const issueIdToFetch = resolveEffectiveIssueId();
    if (!issueIdToFetch) {
      setIsSubmissionLoading(false);
      setSubmission(null);

      return;
    }

    const controller = new AbortController();
    const signal = controller.signal;

    const doFetch = async () => {
      let keepLoading = false;
      setIsSubmissionLoading(true);
      setSubmission(undefined);

      try {
        if (isAuthenticated) {
          const result = await fetchAuthenticatedFlow(
            issueIdToFetch,
            crmToken ?? null,
            signal,
            login,
            showSubmissionCreatedToast
          );
          if (result.loginRedirectTriggered) {
            keepLoading = true;

            return;
          }
          const resolvedSubmission =
            result.submission && result.viewMode === 'private'
              ? { ...result.submission, isSubmitter: true }
              : result.submission;
          setSubmission(resolvedSubmission);
          setViewMode(result.viewMode);
          if (resolvedSubmission && (location.state?.source === 'list' || location.state?.source === 'popup')) {
            trackPublicMapSubmissionDetailViewed(
              isAuthenticated,
              resolvedSubmission.category.name,
              resolvedSubmission.issueSummary.status,
              resolvedSubmission.followerCount ?? 0,
              location.state.source
            );
          }
        } else {
          if (!crmToken) {
            history.replace(PrivateSubmissionAppRoutes.MISSING_TOKEN_ERROR);

            return;
          }
          const result = await fetchUnauthenticatedFlow(issueIdToFetch, crmToken, signal, login);
          if (result.loginRedirectTriggered) {
            keepLoading = true;

            return;
          }
          setSubmission(result.submission);
          setViewMode(result.viewMode);
          if (result.submission && (location.state?.source === 'list' || location.state?.source === 'popup')) {
            trackPublicMapSubmissionDetailViewed(
              isAuthenticated,
              result.submission.category.name,
              result.submission.issueSummary.status,
              result.submission.followerCount ?? 0,
              location.state.source
            );
          }
        }
      } catch (err: any) {
        if (isAbortError(err)) {
          keepLoading = true;

          return;
        }
        setSubmission(null);
      } finally {
        if (!keepLoading) {
          setIsSubmissionLoading(false);
        }
      }
    };

    doFetch();

    return () => controller.abort();
  }, [isAuthResolved, isAuthenticated, crmToken, resolveEffectiveIssueId, history, login, showSubmissionCreatedToast]);

  // Process pending follow intent after auth (e.g. user clicked Follow on map/detail, then completed auth)
  useEffect(() => {
    if (!submission || !isAuthenticated || !crmToken) {
      return;
    }
    const currentIssueId = effectiveCrmIssueId ?? String(submission.id ?? '');
    if (!currentIssueId) return;
    const pendingId = localStorage.getItem(PENDING_FOLLOW_ISSUE_ID);
    if (!pendingId) {
      return;
    }
    if (pendingId !== currentIssueId) {
      clearPendingFollowIntent({ clearCrmIssueId: true });
      return;
    }
    // Clear immediately to prevent re-entry and double API calls
    clearPendingFollowIntent({ clearCrmIssueId: true });

    followPublicMapIssue(crmToken, Number(currentIssueId))
      .then(() => fetchPublicMapIssueById(crmToken, currentIssueId))
      .then((refreshed) => {
        setSubmission((prev) =>
          prev ? { ...prev, ...refreshed, isFollowing: true } : prev
        );
      })
      .catch(() => {
        // Silent fail; user can retry by clicking Follow again
      });
  }, [submission, isAuthenticated, crmToken, effectiveCrmIssueId]);

  const isLoading = !isAuthResolved || isSubmissionLoading;

  if (isLoading) {
    return <Loading show />;
  }

  return (
    <PrivateSubmissionLayout>
      {showSuccessBanner && (
        <div className="user-profile__alert user-profile__alert--success" role="status" aria-live="polite">
          <div className="alert-content">
            <i className="fa fa-check-circle" aria-hidden="true"></i>
            <p>Your submission was successfully created.</p>
          </div>
          <button type="button" onClick={() => setShowSuccessBanner(false)} className="close-btn" aria-label="Close">
            ×
          </button>
        </div>
      )}
      {submission ? (
        <PrivateSubmissionDetails
          submission={submission}
          showName={viewMode !== 'public'}
          backURL={viewMode === 'public' && crmToken ? getPublicMapUrl(crmToken) : undefined}
          showFollowFeature={flags.isPublicMapEnabled}
          hideUpdates={viewMode === 'public' && isAuthenticated && hasPublicMessages}
          onFollowClick={handleFollowClick}
          onUnfollowClick={handleUnfollowClick}
        >
          {viewMode === 'private' && (
            <PrivateSubmissionCommunication
              submission={submission as ICustomerSubmissionResponse}
            />
          )}
          {viewMode === 'public' && isAuthenticated && (
            <PrivateSubmissionCommunication
              submission={submission as ICustomerSubmissionResponse}
              readOnly
              onMessagesLoaded={setHasPublicMessages}
            />
          )}
        </PrivateSubmissionDetails>
      ) : (
        <div className="submission-detail__container">
          <h1>Request Not Found</h1>
          <p>Unable to load the requested submission. Please try again or contact support.</p>
        </div>
      )}

      <FollowAuthModal
        isOpen={isFollowAuthModalOpen}
        onClose={() => {
          clearPendingFollowIntent();
          setIsFollowAuthModalOpen(false);
        }}
        redirectScreen={AuthScreen.ISSUE}
      />
    </PrivateSubmissionLayout>
  );
}
